import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowUp } from "lucide-react";
import moment from "moment-timezone";
import { List, Card } from "semantic-ui-react";

import LogItem from "components/LogItem";
import { NoteEditingProvider } from "components/NoteEditingContext";
import { NotesSkeleton } from "components/NoteItemSkeleton";
import { Alert, AlertAction, AlertTitle } from "components/ui/alert";
import { Button } from "components/ui/button";
import { Spinner } from "components/ui/spinner";
import { createDateTimeFormatter, sortByKey } from "components/helpers";
import { AuditLogHeader } from "components/views/AuditLogHeader";
import { useActivityCategories } from "hooks/useActivityCategories";
import { useAuditLogFeed } from "hooks/useAuditLogFeed";
import { useAuditLogFilters } from "hooks/useAuditLogFilters";
import { useInfiniteScrollSentinel } from "hooks/useInfiniteScrollSentinel";
import { useScrolledPast } from "hooks/useScrolledPast";
import CallDispositionService from "services/CallDispositions";

import "styles/tab_view.scss";
import "styles/audit_log.scss";

const formatDateForGrouping = createDateTimeFormatter({ format: "YYYYMM" });

function groupLogByDate(log) {
  const groups = log.reduce((groups, item) => {
    const date = formatDateForGrouping(item.created_at);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(item);
    return groups;
  }, {});
  return Object.keys(groups).map(date => {
    return {
      date,
      items: groups[date],
    };
  });
}

function formatDateGroup(date) {
  return moment(date, "YYYYMM").format("MMMM YYYY");
}

function AuditLog({
  modelId: modelIdStr,
  modelType,
  slug,
  auditLogId,
  updateFlag,
  allowManualActivity = true,
}) {
  const modelId = Number(modelIdStr);

  const {
    filters: filterDescriptors,
    isLoading: filterOptionsLoading,
    isError: filterOptionsFailed,
  } = useAuditLogFilters({ modelType, slug });

  const filterKeys = useMemo(
    () => filterDescriptors.map(descriptor => descriptor.key),
    [filterDescriptors]
  );

  const {
    log,
    loaded,
    isLoadError,
    isEmpty,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isNextPageError,
    activeItemId,
    queryParams,
    hasFilters,
    refetch: refetchLog,
    applyFilters,
  } = useAuditLogFeed({
    modelId,
    modelType,
    slug,
    auditLogId,
    updateFlag,
    filterKeys,
  });

  const sentinelRef = useInfiniteScrollSentinel({
    hasNextPage,
    isFetchingNextPage,
    isError: isNextPageError,
    fetchNextPage,
    loadedCount: log.length,
  });

  const { markerRef, scrolledPast, scrollToMarker } = useScrolledPast();

  const { categories, refetchCategories } = useActivityCategories();

  const { data: dispositions = [] } = useQuery({
    queryKey: ["call-dispositions-for-filters", { include_callback: true }],
    queryFn: () =>
      CallDispositionService.getDispositionsForFilters({
        include_callback: true,
      }),
  });

  const groupedLog = sortByKey(groupLogByDate(log), "date", true);

  return (
    <>
      <List className="auditLog tabView">
        <div ref={markerRef} />
        <AuditLogHeader
          modelId={modelId}
          modelType={modelType}
          slug={slug}
          allowManualActivity={allowManualActivity}
          categories={categories}
          refetchCategories={refetchCategories}
          filters={filterDescriptors}
          filterKeys={filterKeys}
          queryParams={queryParams}
          filtersLoading={filterOptionsLoading}
          filtersFailed={filterOptionsFailed}
          onApplyFilters={applyFilters}
          onLogChange={refetchLog}
        />

        {isLoadError ? (
          <List.Item>
            <Alert variant="destructive">
              <AlertTitle>Could not load the activity log.</AlertTitle>
              <AlertAction>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchLog()}
                >
                  Retry
                </Button>
              </AlertAction>
            </Alert>
          </List.Item>
        ) : !loaded ? (
          <List.Item>
            <NotesSkeleton />
          </List.Item>
        ) : isEmpty ? (
          <List.Item className="no-results">
            <p>
              {hasFilters
                ? "No activity matches your filters."
                : `Nothing to show for this ${modelType}.`}
            </p>
          </List.Item>
        ) : (
          <NoteEditingProvider>
            {groupedLog.map(({ date, items }) => (
              <List.Item key={date}>
                <List.Header>{formatDateGroup(date)}</List.Header>
                <List.Content>
                  <Card.Group>
                    {items.map(item => (
                      <LogItem
                        key={item.id}
                        item={item}
                        modelType={modelType}
                        modelId={modelId}
                        slug={slug}
                        categories={categories}
                        activeItem={item.id === activeItemId}
                        dispositions={dispositions}
                        refreshLog={refetchLog}
                        feedFilters={queryParams}
                      />
                    ))}
                  </Card.Group>
                </List.Content>
              </List.Item>
            ))}
          </NoteEditingProvider>
        )}

        {loaded && hasNextPage && (
          <List.Item>
            <div
              ref={sentinelRef}
              className="tw:flex tw:items-center tw:justify-center tw:gap-2 tw:py-4 tw:text-sm tw:text-(--color-text-muted)"
            >
              {isNextPageError ? (
                <>
                  <span>Could not load older activity.</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchNextPage()}
                  >
                    Retry
                  </Button>
                </>
              ) : (
                isFetchingNextPage && <Spinner />
              )}
            </div>
          </List.Item>
        )}

        {loaded && !isEmpty && !hasNextPage && (
          <List.Item>
            <div className="tw:flex tw:items-center tw:gap-3 tw:py-4 tw:text-sm tw:text-(--color-text-muted)">
              <span className="tw:h-px tw:flex-1 tw:bg-(--color-border)" />
              <span>No more activity to show.</span>
              <span className="tw:h-px tw:flex-1 tw:bg-(--color-border)" />
            </div>
          </List.Item>
        )}

        {/* Sticky, not fixed: the log sits in a different scroll container
            per layout, and sticky pins the button to whichever one it is.
            The zero height keeps it from adding space after the last entry. */}
        {scrolledPast && (
          <div className="tw:pointer-events-none tw:sticky tw:bottom-4 tw:flex tw:h-0 tw:items-end tw:justify-end">
            <Button
              variant="outline"
              size="icon-lg"
              aria-label="Back to top"
              title="Back to top"
              className="tw:pointer-events-auto tw:rounded-full tw:shadow-md"
              onClick={scrollToMarker}
            >
              <ArrowUp />
            </Button>
          </div>
        )}
      </List>
    </>
  );
}

export default AuditLog;
